// الإقرار الضريبي (ضريبة القيمة المضافة) — بالتصميم الموحّد
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { Box, Paper, Typography, CircularProgress } from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import StoreOutlinedIcon from '@mui/icons-material/StoreOutlined';

import axios from '../../axios';
import {
  ReportPage, ReportFilters, ReportSelect, useReportDateRange, useAllowedBranches, fmtMoney,
} from '../../components/Reports/ReportKit';
import { PrintSheet, PrintSummary, P, useReportPrint, useCompanyInfo, fmtDate } from '../../components/Reports/PrintKit';

const BORDER = '#e5e7eb';
const n = (v) => Number(v) || 0;

// بنود الإقرار: [label, valueKey, vatKey, kind]
const buildRows = (r) => {
  const s = r?.sales || {};
  const p = r?.purchases || {};
  return {
    sales: [
      ['المبيعات الخاضعة للضريبة', s.taxableSales, s.vatCollected],
      ['مرتجع المبيعات', s.salesReturns, s.vatSalesReturn, 'minus'],
      ['صافي المبيعات', s.netSales, s.netVATCollected, 'total'],
    ],
    purchases: [
      ['المشتريات الخاضعة للضريبة', p.taxablePurchases, p.vatPaid],
      ['مرتجع المشتريات', p.purchaseReturns, p.vatPurchaseReturn, 'minus'],
      ['صافي المشتريات', p.netPurchases, p.netVATPaid, 'total'],
    ],
    netBase: n(s.netSales) - n(p.netPurchases),
    vatDue: n(r?.vatSummary?.vatDue ?? (n(s.netVATCollected) - n(p.netVATPaid))),
    outputVat: n(s.netVATCollected),
    inputVat: n(p.netVATPaid),
  };
};

function VatSection({ title, icon, color, rows }) {
  return (
    <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: '14px', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.1, borderBottom: `1px solid ${BORDER}`, bgcolor: '#fafbfc' }}>
        <Box sx={{ width: 28, height: 28, borderRadius: '8px', display: 'grid', placeItems: 'center', bgcolor: `${color}1a`, color, '& svg': { fontSize: 17 } }}>{icon}</Box>
        <Typography sx={{ fontWeight: 800, fontSize: 14.5, flex: 1 }}>{title}</Typography>
      </Box>
      <Box component="table" sx={{
        width: '100%', borderCollapse: 'collapse', fontSize: 14,
        '& th': { fontWeight: 800, color: '#475569', fontSize: 13, textAlign: 'right', px: 2, py: .9, borderBottom: `1px solid ${BORDER}` },
        '& td': { px: 2, py: 1.1, borderBottom: '1px solid #f1f5f9' },
        '& .num': { direction: 'ltr', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
      }}>
        <thead><tr><th>البند</th><th style={{ width: '30%' }}>المبلغ (قبل الضريبة)</th><th style={{ width: '26%' }}>الضريبة</th></tr></thead>
        <tbody>
          {rows.map(([label, value, vat, kind]) => (
            <tr key={label} style={kind === 'total' ? { background: `${color}0f` } : undefined}>
              <td style={{ fontWeight: kind === 'total' ? 800 : 600, color: kind === 'minus' ? '#be123c' : undefined }}>
                {kind === 'minus' ? '(−) ' : ''}{label}
              </td>
              <td className="num" style={{ fontWeight: kind === 'total' ? 800 : 600 }}>{fmtMoney(value)}</td>
              <td className="num" style={{ fontWeight: 800, color: kind === 'total' ? color : undefined }}>{fmtMoney(vat)}</td>
            </tr>
          ))}
        </tbody>
      </Box>
    </Paper>
  );
}

export default function VatReportPage() {
  const companyId = parseInt(localStorage.getItem('companyId'), 10);
  const range = useReportDateRange('month');
  const { branchOptions, defaultBranch, ready, branchName } = useAllowedBranches(companyId);
  const company = useCompanyInfo();

  const [branchId, setBranchId] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (ready) setBranchId(defaultBranch); }, [ready, defaultBranch]);

  const fetchReport = useCallback(async () => {
    if (!companyId || !ready) return;
    setLoading(true);
    try {
      const res = await axios.get('/VatTypes/vat-report', {
        params: { companyId, branchId: branchId || null, fromDate: range.dateFrom, toDate: range.dateTo },
      });
      setReport(res.data);
    } catch (err) {
      console.error(err);
      toast.error('فشل في جلب الإقرار الضريبي');
    } finally {
      setLoading(false);
    }
  }, [companyId, ready, branchId, range.dateFrom, range.dateTo]);

  useEffect(() => {
    const id = setTimeout(fetchReport, 300);
    return () => clearTimeout(id);
  }, [fetchReport]);

  const v = buildRows(report);
  const due = v.vatDue;
  const branchLabel = branchId ? branchName(branchId) : 'كل الفروع';

  const exportExcel = () => {
    if (!report) return;
    const aoa = [
      ['الإقرار الضريبي', `${fmtDate(range.dateFrom)} - ${fmtDate(range.dateTo)}`, branchLabel],
      [],
      ['البند', 'المبلغ (قبل الضريبة)', 'الضريبة'],
      ...v.sales.map(([l, a, t]) => [l, n(a), n(t)]),
      [],
      ...v.purchases.map(([l, a, t]) => [l, n(a), n(t)]),
      [],
      [due >= 0 ? 'صافي الضريبة المستحقة للدفع' : 'صافي الضريبة القابلة للاسترداد', v.netBase, due],
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 34 }, { wch: 20 }, { wch: 16 }];
    const wb = XLSX.utils.book_new();
    wb.Workbook = { Views: [{ RTL: true }] };
    XLSX.utils.book_append_sheet(wb, ws, 'الإقرار');
    XLSX.writeFile(wb, `الإقرار-الضريبي-${range.dateFrom}_${range.dateTo}.xlsx`);
  };

  const printRef = useRef(null);
  const handlePrint = useReportPrint(printRef, 'الإقرار-الضريبي');

  return (
    <ReportPage
      title="الإقرار الضريبي"
      icon={<ReceiptLongOutlinedIcon />}
      period={range}
      badges={[{ label: branchLabel, icon: <StoreOutlinedIcon /> }]}
      actions={[
        { label: 'تحديث', icon: <RefreshIcon />, onClick: fetchReport, disabled: loading },
        { label: 'Excel', icon: <FileDownloadOutlinedIcon />, onClick: exportExcel, disabled: !report },
        { label: 'طباعة', icon: <PrintOutlinedIcon />, onClick: handlePrint, disabled: !report, primary: true },
      ]}
      stats={report ? [
        { label: 'صافي المبيعات', value: fmtMoney(report?.sales?.netSales), icon: <PointOfSaleOutlinedIcon />, color: 'primary' },
        { label: 'ضريبة المخرجات', value: fmtMoney(v.outputVat), icon: <PointOfSaleOutlinedIcon />, color: 'info' },
        { label: 'ضريبة المدخلات', value: fmtMoney(v.inputVat), icon: <ShoppingBagOutlinedIcon />, color: 'warning' },
        {
          label: due >= 0 ? 'الضريبة المستحقة للدفع' : 'ضريبة قابلة للاسترداد', value: fmtMoney(Math.abs(due)),
          icon: <AccountBalanceOutlinedIcon />, color: due >= 0 ? 'error' : 'success', highlight: true,
        },
      ] : []}
      filters={
        <ReportFilters
          range={range}
          onApply={fetchReport}
          onReset={() => { range.reset(); setBranchId(defaultBranch); }}
          loading={loading}
          activeCount={branchId && branchId !== defaultBranch ? 1 : 0}
        >
          <ReportSelect
            label="الفرع" value={branchId} onChange={setBranchId} options={branchOptions}
            allLabel={defaultBranch ? null : 'كل الفروع'}
          />
        </ReportFilters>
      }
    >
      {report && (
        <Box sx={{ display: 'none' }}>
          <PrintSheet ref={printRef} title="الإقرار الضريبي" subtitle={branchLabel}
            period={`من ${fmtDate(range.dateFrom)} إلى ${fmtDate(range.dateTo)}`} company={company}>
            <PrintSummary items={[
              ['ضريبة المخرجات', fmtMoney(v.outputVat)],
              ['ضريبة المدخلات', fmtMoney(v.inputVat)],
              [due >= 0 ? 'المستحق للدفع' : 'قابل للاسترداد', fmtMoney(Math.abs(due))],
            ]} />
            {[['المبيعات (ضريبة المخرجات)', v.sales], ['المشتريات (ضريبة المدخلات)', v.purchases]].map(([title, rows]) => (
              <React.Fragment key={title}>
                <div style={P.section}>{title}</div>
                <table style={P.table}>
                  <thead><tr><th style={P.th}>البند</th><th style={{ ...P.th, width: 150 }}>المبلغ</th><th style={{ ...P.th, width: 120 }}>الضريبة</th></tr></thead>
                  <tbody>
                    {rows.map(([l, a, t, kind]) => (
                      <tr key={l} style={kind === 'total' ? { fontWeight: 800, background: '#f3f4f6' } : undefined}>
                        <td style={P.td}>{kind === 'minus' ? '(−) ' : ''}{l}</td>
                        <td style={P.num}>{fmtMoney(a)}</td>
                        <td style={P.num}>{fmtMoney(t)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </React.Fragment>
            ))}
            <div style={{ border: '2px solid #111827', borderRadius: 8, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 800 }}>
              <span>{due >= 0 ? 'صافي الضريبة المستحقة للدفع' : 'صافي الضريبة القابلة للاسترداد'}</span>
              <span style={{ direction: 'ltr' }}>{fmtMoney(Math.abs(due))}</span>
            </div>
          </PrintSheet>
        </Box>
      )}

      {loading && !report ? (
        <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 260 }}><CircularProgress /></Box>
      ) : report && (
        <Box sx={{ display: 'grid', gap: 1.5, opacity: loading ? .6 : 1, transition: 'opacity .2s' }}>
          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, alignItems: 'start' }}>
            <VatSection title="المبيعات — ضريبة المخرجات" icon={<PointOfSaleOutlinedIcon />} color="#4f46e5" rows={v.sales} />
            <VatSection title="المشتريات — ضريبة المدخلات" icon={<ShoppingBagOutlinedIcon />} color="#d97706" rows={v.purchases} />
          </Box>

          <Paper elevation={0} sx={{
            borderRadius: '16px', p: { xs: 2, sm: 2.5 }, color: '#fff', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
            background: due >= 0 ? 'linear-gradient(120deg,#be123c,#f43f5e)' : 'linear-gradient(120deg,#047857,#10b981)',
          }}>
            <AccountBalanceOutlinedIcon sx={{ fontSize: 40, opacity: .9 }} />
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography sx={{ fontWeight: 800, fontSize: 18 }}>
                {due >= 0 ? 'صافي الضريبة المستحقة للدفع' : 'صافي الضريبة القابلة للاسترداد'}
              </Typography>
              <Typography sx={{ fontSize: 13.5, opacity: .9 }}>
                ضريبة المخرجات {fmtMoney(v.outputVat)} − ضريبة المدخلات {fmtMoney(v.inputVat)}
              </Typography>
            </Box>
            <Typography sx={{ fontWeight: 900, fontSize: { xs: 26, sm: 32 }, direction: 'ltr' }}>{fmtMoney(Math.abs(due))}</Typography>
          </Paper>

          <Typography sx={{ fontSize: 12.5, color: '#64748b' }}>
            الأرقام من الفواتير المعتمدة في النظام. راجعها مع حساباتك قبل تقديم الإقرار في بوابة هيئة الزكاة والضريبة والجمارك.
          </Typography>
        </Box>
      )}
    </ReportPage>
  );
}
